import { useEffect, useMemo, useState } from "react";
import {
  type SupportTicketAction,
  useForwardAction,
  useInternalNoteAction,
  useReplyAction,
  useReplyAllAction,
} from "../actions/use-support-ticket-actions";

export const replyModes = [
  "reply",
  "replyAll",
  "forward",
  "internalNote",
] as const;
export type ReplyMode = (typeof replyModes)[number];

export interface UseReplyModeResult {
  replyMode: ReplyMode;
  setReplyMode: (mode: ReplyMode) => void;
  activeReplyAction: SupportTicketAction;
  replyModeActions: Record<ReplyMode, SupportTicketAction>;
}

export function useReplyMode(
  initialMode: ReplyMode = "reply",
): UseReplyModeResult {
  const [replyMode, setReplyMode] = useState<ReplyMode>(initialMode);

  useEffect(() => {
    setReplyMode(initialMode);
  }, [initialMode]);

  const replyAction = useReplyAction(() => setReplyMode("reply"));
  const replyAllAction = useReplyAllAction(() => setReplyMode("replyAll"));
  const forwardAction = useForwardAction(() => setReplyMode("forward"));
  const internalNoteAction = useInternalNoteAction(() =>
    setReplyMode("internalNote"),
  );

  const replyModeActions: Record<ReplyMode, SupportTicketAction> = useMemo(
    () => ({
      reply: replyAction,
      replyAll: replyAllAction,
      forward: forwardAction,
      internalNote: internalNoteAction,
    }),
    [replyAction, replyAllAction, forwardAction, internalNoteAction],
  );

  const activeReplyAction = replyModeActions[replyMode];

  return { replyMode, setReplyMode, activeReplyAction, replyModeActions };
}
